import React, { memo, useMemo, useEffect, useCallback, useRef } from 'react';
import { View, Platform, StyleSheet, LayoutAnimation } from 'react-native';
import { rem } from 'design-system';
import { selectSecclGiaReadyMadeFunds, selectWalkthroughAccount, } from 'features/invest/reducers/selectors';
import { setInvestWalkthroughStep, setInvestWalkthroughStatus, } from 'actions/walkthrough';
import { useMainStackRoute, useMainStackNavigation, } from 'utils/types/navigationV6';
import { Amplitude } from 'utils';
import { useFocusEffect } from 'hooks';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { selectInvestWalkthroughStep } from 'reducers/selectors/walkthrough';
import { setSelectedTradingAccountId } from 'features/invest/actions';
import WalkthroughOverlay from './WalkthroughOverlay';
import WalkthroughBubbleArrow from './WalkthroughBubbleArrow';
import WalkthroughBubbleContent from './WalkthroughBubbleContent';
import useInvestWalkthroughItems from './invest/useInvestWalkthroughItems';
const WalkthroughContent = ({ itemPositions = {}, scrollRef, scrollHeight, isInTab = true, }) => {
    const { name: routeName, key: routeKey } = useMainStackRoute();
    const colors = useColors();
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const { paddingHorizontalStyle, paddingHorizontal } = useAppFrameDimensions(isInTab);
    const currentStep = useAppSelector(selectInvestWalkthroughStep);
    const isInOnboarding = useAppSelector((state) => state.walkthrough.investWalkthrough?.isInOnboarding);
    const readyMadeFundsAssetIds = useAppSelector(selectSecclGiaReadyMadeFunds);
    const items = useInvestWalkthroughItems(itemPositions, isInTab);
    const item = items[currentStep];
    const isMounted = useRef(false);
    useEffect(() => {
        if (item?.screen) {
            navigation.setOptions({
                headerTintColor: colors.elements.primaryOnModal,
            });
        }
    }, [item?.screen, colors.elements.primaryOnModal]);
    useEffect(() => () => {
        // the screen may already have been removed when the walkthrough ends
        if (navigation.getState()?.routes.some((r) => r.key === routeKey)) {
            navigation.setOptions({
                headerTintColor: colors.elements.primary,
            });
        }
    }, [colors.elements.primary, routeKey]);
    const walkthroughAccount = useAppSelector(selectWalkthroughAccount);
    const goToScreen = useCallback((screen) => {
        // these later steps require a trading account to be selected
        if (screen === 'InvestPortfolio' ||
            screen === 'InvestDiscover' ||
            screen === 'InvestStock') {
            dispatch(setSelectedTradingAccountId(walkthroughAccount?.account.id));
        }
        if (screen === 'InvestStock') {
            const assetId = readyMadeFundsAssetIds?.[0];
            if (assetId) {
                navigation.navigateDeprecated('InvestStock', { assetId });
            }
        }
        else {
            navigation.navigateDeprecated(screen);
        }
    }, [readyMadeFundsAssetIds, walkthroughAccount?.account.id]);
    useFocusEffect(useCallback(() => {
        if (isMounted.current) {
            if (item?.screen && item.screen !== routeName) {
                goToScreen(item.screen);
            }
        }
        else {
            isMounted.current = true;
        }
    }, [goToScreen, item?.screen, routeName]));
    const goToStep = useCallback((step) => {
        const nextStep = items[step];
        const isSameScreen = nextStep.screen === item?.screen;
        if (nextStep) {
            dispatch(setInvestWalkthroughStep({
                step,
            }));
            if (isSameScreen) {
                if (Platform.OS !== 'android') {
                    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
                }
            }
            else {
                goToScreen(nextStep.screen);
            }
        }
    }, [goToScreen, item?.screen, items]);
    useEffect(() => {
        if (item?.screen === routeName) {
            scrollRef?.current?.scrollTo({
                y: (item.position?.y || 0) -
                    // eslint-disable-next-line no-nested-ternary
                    (item.overrideScrollYOffset
                        ? item.overrideScrollYOffset
                        : item.bubblePosition === 'top'
                            ? 300
                            : 45),
                x: 0,
                animated: true,
            });
        }
    }, [item, routeName, scrollRef]);
    const nextStep = useCallback(() => {
        goToStep(currentStep + 1);
    }, [currentStep, goToStep]);
    const previousStep = useCallback(() => {
        goToStep(currentStep - 1);
    }, [currentStep, goToStep]);
    const onPressReset = useCallback(() => {
        Amplitude.logEvent('Restart Invest Walkthrough');
        goToStep(0);
    }, [goToStep]);
    const dispatch = useAppDispatch();
    const onPressDone = useCallback(() => {
        Amplitude.logEvent('Completed Invest Walkthrough');
        dispatch(setInvestWalkthroughStatus('complete'));
        navigation.popToTop();
        if (isInOnboarding) {
            navigation.navigateDeprecated('InvestWalkthroughEnded');
        }
    }, [isInOnboarding]);
    const containerStyle = useMemo(() => {
        if (!item)
            return undefined;
        return item.bubblePosition === 'top'
            ? {
                bottom: scrollHeight -
                    (item.overrideHeight || item.position?.height || 0) -
                    (item.position?.y || 0),
            }
            : {
                top: item.position?.y || 0,
            };
    }, [item, scrollHeight]);
    if (!item || !containerStyle) {
        return null;
    }
    const arrow = (<View style={item.floatBubbleRight
            ? { marginLeft: 2 * paddingHorizontal }
            : paddingHorizontalStyle}>
      <WalkthroughBubbleArrow style={[
            item.arrowPosition === 'start' && styles.bubbleArrowStart,
            item.arrowPosition === 'end' && styles.bubbleArrowEnd,
            item.arrowPosition === 'cardButtonLeft' &&
                styles.bubbleArrowCardButtonLeft,
            item.arrowPosition === 'cardButtonRight' &&
                styles.bubbleArrowCardButtonRight,
            item.arrowPosition === 'center' && styles.bubbleArrowCenter,
            item.bubblePosition === 'top'
                ? styles.bubbleArrowTop
                : styles.bubbleArrowBottom,
            item.arrowStyles,
        ]}/>
    </View>);
    return (<View style={styles.container}>
      <WalkthroughOverlay context="invest" style={styles.overlay}/>
      {routeName === item?.screen && (<View style={[styles.itemContainer, containerStyle]}>
          {item.bubblePosition === 'bottom' ? (<>
              {item.component}
              {arrow}
            </>) : null}
          <View style={item.floatBubbleRight
                ? { paddingLeft: 2 * paddingHorizontal }
                : paddingHorizontalStyle}>
            <WalkthroughBubbleContent currentStep={currentStep} numberOfSteps={items.length} title={item.title} description={item.description} onPressDone={onPressDone} onPressNext={nextStep} onPressPrevious={previousStep} onPressReset={onPressReset}/>
          </View>
          {item.bubblePosition === 'top' ? (<>
              {arrow}
              {item.component}
            </>) : null}
        </View>)}
    </View>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        ...StyleSheet.absoluteFillObject,
        zIndex: 100,
    },
    overlay: {
        top: -1000,
        bottom: -1000,
    },
    itemContainer: {
        width: '100%',
        position: 'absolute',
    },
    bubbleArrowStart: {
        marginLeft: rem(40),
        alignSelf: 'flex-start',
    },
    bubbleArrowEnd: {
        marginRight: rem(40),
        alignSelf: 'flex-end',
    },
    bubbleArrowCenter: {
        alignSelf: 'center',
        marginLeft: 0,
        marginRight: 0,
    },
    bubbleArrowCardButtonLeft: {
        marginLeft: rem(80),
        alignSelf: 'flex-start',
    },
    bubbleArrowCardButtonRight: {
        marginRight: rem(80),
        alignSelf: 'flex-end',
    },
    bubbleArrowTop: {
        transform: [{ rotate: '-180deg' }],
        marginTop: rem(-1),
        marginBottom: rem(8),
    },
    bubbleArrowBottom: {
        marginBottom: rem(-1),
        marginTop: rem(8),
    },
}));
export default memo(WalkthroughContent);
